import React, { useState, useEffect } from 'react';
import {
  Modal,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Card,
  CardBody,
  Spinner
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';
import { formatCreatedDate } from '../constants/utils';
import { AssetListTable, WorkOrdersTable } from './Common';
import AssetModal from './AssetModal';

const SparePartsModal = ({
  isOpen,
  toggle,
  sparePartData,
  loading = false,
  onEdit,
  onRestock
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('details');
  const [inventoryLog, setInventoryLog] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null });

  const handleEdit = () => {
    if (onEdit && sparePartData) {
      onEdit(sparePartData);
      toggle();
    }
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300
      }
    })
  };

  // Fetch inventory log when tab is opened
  useEffect(() => {
    if (activeTab === 'inventory' && sparePartData?.id) {
      fetchInventoryLog();
    }
  }, [activeTab, sparePartData?.id]);

  // Fetch work orders when tab is opened
  useEffect(() => {
    if (activeTab === 'workOrders' && sparePartData?.id) {
      fetchWorkOrders();
    }
  }, [activeTab, sparePartData?.id]);

  const fetchInventoryLog = async () => {
    if (!sparePartData?.id) return;

    setLoadingInventory(true);
    try {
      const { data } = await api.get(`spare-parts/inventory/all?sparePartId=${sparePartData.id}`);
      setInventoryLog(data?.response || []);
    } catch (error) {
      console.error('Error fetching inventory log:', error);
      setInventoryLog([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  const fetchWorkOrders = async () => {
    if (!sparePartData?.id) return;

    setLoadingWorkOrders(true);
    try {
      const { data } = await api.get(`spare-parts/work-orders?sparePartId=${sparePartData.id}`);
      setWorkOrders(data?.response || []);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setWorkOrders([]);
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  // Split inventory log into restock and consumption sections
  const restockRecords = inventoryLog.filter(item =>
    item.sparePartStatus === 'IN_STOCK' || item.sparePartStatus === 'DISPOSED'
  );
  const consumptionRecords = inventoryLog.filter(item =>
    item.sparePartStatus === 'INSTALLED'
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="modal-dialog-centered"
    >
      {/* Modal Header with gradient background */}
      <div
        className="modal-header border-0"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '1.5rem 2rem'
        }}
      >
        <div className="w-100">
          <div className="d-flex justify-content-between align-items-start">
            <div className="text-white">
              <h4 className="mb-2 fw-bold">
                <i className="bx bx-wrench me-2"></i>
                {sparePartData?.name || t('business.components.spare_parts_modal.title_default', 'Spare Part Details')}
              </h4>
              <div className="d-flex align-items-center gap-3">
                <span className="badge bg-white bg-opacity-25 text-white">
                  <i className="bx bx-barcode me-1"></i>
                  {sparePartData?.manufacturerPartNumber || 'N/A'}
                </span>
                {sparePartData?.criticality && (
                  <Badge
                    color={
                      sparePartData.criticality === 'HIGH' ? 'danger' :
                      sparePartData.criticality === 'MEDIUM' ? 'warning' :
                      'success'
                    }
                    className="px-2 py-1 text-uppercase"
                  >
                    {t(`shared.${sparePartData.criticality.toLowerCase()}`, sparePartData.criticality)}
                  </Badge>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 ms-auto">
              {onRestock && sparePartData && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={() => { onRestock(sparePartData); toggle(); }}
                  title={t('business.spare_parts.restock', 'Restock')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    color: '#495057',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <FeatherIcon icon="plus-circle" size={16} />
                  <span>{t('business.spare_parts.restock', 'Restock')}</span>
                </button>
              )}
              {onEdit && sparePartData && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={handleEdit}
                  title={t('shared.button_edit_spare_part', 'Edit Spare Part')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    color: '#495057',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <FeatherIcon icon="edit-3" size={16} />
                  <span>{t('shared.button_edit_spare_part', 'Edit Spare Part')}</span>
                </button>
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label={t('shared.close', 'Close')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Navigation */}
      <Nav
        tabs
        className="border-bottom-0 px-4 pt-3 bg-light"
        style={{ borderRadius: '0' }}
      >
        <NavItem>
          <NavLink
            className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
            style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '12px 20px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <i className="bx bx-info-circle me-2"></i>
            {t('shared.details', 'Details')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`nav-link-modern ${activeTab === 'assets' ? 'active' : ''}`}
            onClick={() => setActiveTab('assets')}
            style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '12px 20px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <i className="bx bx-package me-2"></i>
            {t('shared.associated_assets', 'Associated Assets')}
            {sparePartData?.asset?.length > 0 && (
              <Badge
                color="info"
                pill
                className="ms-2"
                style={{ fontSize: '10px' }}
              >
                {sparePartData.asset.length}
              </Badge>
            )}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`nav-link-modern ${activeTab === 'workOrders' ? 'active' : ''}`}
            onClick={() => setActiveTab('workOrders')}
            style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '12px 20px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <i className="bx bx-task me-2"></i>
            {t('shared.work_orders', 'Work Orders')}
            {workOrders.length > 0 && (
              <Badge
                color="primary"
                pill
                className="ms-2"
                style={{ fontSize: '10px' }}
              >
                {workOrders.length}
              </Badge>
            )}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`nav-link-modern ${activeTab === 'inventory' ? 'active' : ''}`}
            onClick={() => setActiveTab('inventory')}
            style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '12px 20px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <i className="bx bx-list-ul me-2"></i>
            {t('shared.inventory_log', 'Inventory Log')}
            {inventoryLog.length > 0 && (
              <Badge
                color="warning"
                pill
                className="ms-2"
                style={{ fontSize: '10px' }}
              >
                {inventoryLog.length}
              </Badge>
            )}
          </NavLink>
        </NavItem>
      </Nav>

      {/* Modal Body */}
      <div
        className="modal-body p-0"
        style={{
          maxHeight: '60vh',
          overflow: 'auto'
        }}
      >
        <TabContent activeTab={activeTab} className="h-100">
          {/* Details Tab */}
          <TabPane tabId="details">
            <div className="p-4">
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="info-icon me-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <i className="bx bx-wrench text-primary fs-5"></i>
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold">{t('business.components.spare_parts_modal.info_title', 'Spare Part Information')}</h5>
                      <p className="text-muted small mb-0">{t('business.components.spare_parts_modal.info_subtitle', 'Complete spare part specifications and details')}</p>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="lg" />
                      <p className="text-muted mt-3">{t('business.components.spare_parts_modal.loading_details', 'Loading spare part details...')}</p>
                    </div>
                  ) : (
                    <Row className="g-4">
                      {/* Stock Summary — only In Stock and Min Stock */}
                      <Col md={12}>
                        <div className="d-flex gap-3 flex-wrap">
                          <div className="info-card bg-light rounded p-3 text-center flex-fill">
                            <label className="text-muted small d-block">{t('business.spare_parts.in_stock_quantity', 'In Stock')}</label>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <span className={`fs-4 fw-bold ${sparePartData?.quantity < sparePartData?.minimumStockLevel ? 'text-danger' : 'text-primary'}`}>
                                {sparePartData?.quantity ?? 0}
                              </span>
                              {sparePartData?.quantity < sparePartData?.minimumStockLevel && (
                                <Badge color="danger">{t('business.spare_parts.low_stock_warning', 'Low Stock')}</Badge>
                              )}
                            </div>
                          </div>
                          <div className="info-card bg-light rounded p-3 text-center flex-fill">
                            <label className="text-muted small d-block">{t('shared.minimum_stock_level', 'Min Stock')}</label>
                            <span className="fs-4 fw-bold text-secondary">{sparePartData?.minimumStockLevel ?? 0}</span>
                          </div>
                        </div>
                      </Col>

                      {/* Basic Information */}
                      <Col md={6}>
                        <div className="info-card bg-light rounded p-3 h-100">
                          <h6 className="text-primary mb-3 fw-semibold">
                            <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                          </h6>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.part_name', 'Part Name')}</label>
                            <div className="fw-medium">{sparePartData?.name || 'N/A'}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.type', 'Type')}</label>
                            <div className="fw-medium">{sparePartData?.sparePartType?.name || sparePartData?.type || 'N/A'}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.location', 'Location')}</label>
                            <div className="fw-medium">{sparePartData?.preferredStorageLocation || 'N/A'}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.part_number', 'Part Number')}</label>
                            <div className="fw-medium">{sparePartData?.manufacturerPartNumber || 'N/A'}</div>
                          </div>
                          <div className="info-item mb-0">
                            <label className="text-muted small">{t('shared.criticality', 'Criticality')}</label>
                            <div>
                              <Badge
                                color={
                                  sparePartData?.criticality === 'HIGH' ? 'danger' :
                                  sparePartData?.criticality === 'MEDIUM' ? 'warning' :
                                  sparePartData?.criticality === 'LOW' ? 'success' : 'secondary'
                                }
                                className="px-2 py-1 text-uppercase"
                              >
                                {sparePartData?.criticality ? t(`shared.${sparePartData.criticality.toLowerCase()}`, sparePartData.criticality) : 'N/A'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Supplier & Storage */}
                      <Col md={6}>
                        <div className="info-card bg-light rounded p-3 h-100">
                          <h6 className="text-success mb-3 fw-semibold">
                            <i className="bx bx-store me-2"></i>{t('business.components.spare_parts_modal.supplier_storage', 'Supplier & Storage')}
                          </h6>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.vendors', 'Vendor')}</label>
                            <div className="fw-medium">{sparePartData?.vendor?.name || 'N/A'}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.manufacturer', 'Manufacturer')}</label>
                            <div className="fw-medium">{sparePartData?.manufacturerName || 'N/A'}</div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.min_stock_level', 'Minimum Stock Level')}</label>
                            <div className="fw-medium">{sparePartData?.minimumStockLevel || 0}</div>
                          </div>
                          <div className="info-item mb-0">
                            <label className="text-muted small">{t('business.spare_parts.average_unit_cost', 'Average Unit Cost')}</label>
                            <div className="fw-medium">
                              {sparePartData?.averageUnitCost != null
                                ? `${sparePartData.currency ? sparePartData.currency + ' ' : ''}${Number(sparePartData.averageUnitCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Description */}
                      {sparePartData?.description && (
                        <Col md={12}>
                          <div className="info-card bg-light rounded p-3">
                            <h6 className="text-info mb-3 fw-semibold">
                              <i className="bx bx-file-blank me-2"></i>{t('shared.description', 'Description')}
                            </h6>
                            <p className="mb-0 text-muted">{sparePartData.description}</p>
                          </div>
                        </Col>
                      )}

                      {/* Images */}
                      {sparePartData?.imageUrls?.length > 0 && (
                        <Col md={12}>
                          <div className="info-card bg-light rounded p-3">
                            <h6 className="text-primary mb-3 fw-semibold">
                              <FeatherIcon icon="image" size={14} className="me-2" />{t('business.spare_parts.images', 'Images')}
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                              {sparePartData.imageUrls.map((url, index) => (
                                <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt={`${index + 1}`} className="rounded" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                                </a>
                              ))}
                            </div>
                          </div>
                        </Col>
                      )}

                      {/* Documents */}
                      {sparePartData?.documentUrls?.length > 0 && (
                        <Col md={12}>
                          <div className="info-card bg-light rounded p-3">
                            <h6 className="text-primary mb-3 fw-semibold">
                              <FeatherIcon icon="file-text" size={14} className="me-2" />{t('business.spare_parts.documents', 'Documents')}
                            </h6>
                            {sparePartData.documentUrls.map((url, index) => (
                              <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center bg-white rounded px-3 py-2 mb-2 text-decoration-none">
                                <FeatherIcon icon="file-text" size={18} className="text-primary me-2" />
                                <span className="text-truncate text-dark" style={{ maxWidth: '400px' }}>{url.split('/').pop()}</span>
                                <FeatherIcon icon="external-link" size={14} className="ms-auto text-muted" />
                              </a>
                            ))}
                          </div>
                        </Col>
                      )}
                    </Row>
                  )}
                </CardBody>
              </Card>
            </div>
          </TabPane>

          {/* Associated Assets Tab — using shared AssetListTable */}
          <TabPane tabId="assets">
            <div className="p-4">
              <AssetListTable
                data={(sparePartData?.asset || []).map(a => ({
                  ...a,
                  assetName: a.name || a.assetName
                }))}
                loading={false}
                onAssetClick={(asset) => setAssetModal({ isOpen: true, userData: asset })}
                emptyMessage={t('shared.no_associated_assets', 'No Associated Assets')}
                excludeColumns={['status', 'location']}
              />
            </div>
          </TabPane>

          {/* Work Orders Tab — using shared WorkOrdersTable */}
          <TabPane tabId="workOrders">
            <div className="p-4">
              {loadingWorkOrders ? (
                <div className="text-center py-5">
                  <Spinner color="primary" size="lg" />
                  <p className="text-muted mt-3">{t('shared.loading', 'Loading...')}</p>
                </div>
              ) : (
                <WorkOrdersTable
                  data={workOrders}
                  columns={['ticketNumber', 'title', 'status', 'priority', 'createdAt']}
                  emptyMessage={t('business.components.spare_parts_modal.no_work_orders_desc', 'No work orders have used this spare part yet')}
                  emptyIcon="tool"
                />
              )}
            </div>
          </TabPane>

          {/* Inventory Log Tab — split into Restock & Consumption */}
          <TabPane tabId="inventory">
            <div className="p-4">
              {loadingInventory ? (
                <div className="text-center py-5">
                  <Spinner color="primary" size="lg" />
                  <p className="text-muted mt-3">{t('shared.loading_inventory_log', 'Loading inventory log...')}</p>
                </div>
              ) : inventoryLog.length > 0 ? (
                <>
                  {/* Restock History Section */}
                  <Card className="border-0 shadow-sm mb-4">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                             style={{ width: '36px', height: '36px' }}>
                          <i className="bx bx-plus-circle text-success fs-5"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">{t('business.spare_parts.restock_history', 'Restock History')}</h6>
                          <small className="text-muted">{restockRecords.length} {t('shared.records', 'record(s)')}</small>
                        </div>
                      </div>

                      {restockRecords.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="px-3 py-3">#</th>
                                <th className="px-3 py-3">{t('shared.reference_number', 'Reference Number')}</th>
                                <th className="px-3 py-3">{t('shared.quantity', 'Quantity')}</th>
                                <th className="px-3 py-3">{t('business.spare_parts.unit_cost', 'Unit Cost')}</th>
                                <th className="px-3 py-3">{t('shared.spare_part_status', 'Status')}</th>
                                <th className="px-3 py-3">{t('business.spare_parts.notes', 'Notes')}</th>
                                <th className="px-3 py-3">{t('shared.created_date', 'Created Date')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {restockRecords.map((item, index) => (
                                <motion.tr key={item.id || index} custom={index} initial="hidden" animate="visible" variants={tableRowVariants}>
                                  <td className="px-3 py-3"><Badge color="light" className="text-dark">{index + 1}</Badge></td>
                                  <td className="px-3 py-3"><span className="fw-medium">{item.referenceNumber || '-'}</span></td>
                                  <td className="px-3 py-3"><Badge color="info" className="px-2 py-1">{item.quantity || 0}</Badge></td>
                                  <td className="px-3 py-3 text-muted">{item.purchasePrice != null ? Number(item.purchasePrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                  <td className="px-3 py-3">
                                    <Badge color={item.sparePartStatus === 'IN_STOCK' ? 'success' : 'warning'} className="px-2 py-1">
                                      {item.sparePartStatus === 'IN_STOCK' ? t('shared.restocked', 'Restocked') : t('shared.adjusted', 'Adjusted')}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-3 text-muted">{item.notes || '-'}</td>
                                  <td className="px-3 py-3 text-muted">{item.createdAt ? formatCreatedDate(item.createdAt) : '-'}</td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted small mb-0">{t('business.spare_parts.no_restock_records', 'No restock records found')}</p>
                      )}
                    </CardBody>
                  </Card>

                  {/* Consumption History Section */}
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                             style={{ width: '36px', height: '36px' }}>
                          <i className="bx bx-minus-circle text-warning fs-5"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">{t('business.spare_parts.consumption_history', 'Consumption History')}</h6>
                          <small className="text-muted">{consumptionRecords.length} {t('shared.records', 'record(s)')}</small>
                        </div>
                      </div>

                      {consumptionRecords.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="px-3 py-3">#</th>
                                <th className="px-3 py-3">{t('shared.reference_number', 'Reference Number')}</th>
                                <th className="px-3 py-3">{t('shared.quantity', 'Quantity')}</th>
                                <th className="px-3 py-3">{t('shared.status', 'Status')}</th>
                                <th className="px-3 py-3">{t('shared.created_date', 'Created Date')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {consumptionRecords.map((item, index) => (
                                <motion.tr key={item.id || index} custom={index} initial="hidden" animate="visible" variants={tableRowVariants}>
                                  <td className="px-3 py-3"><Badge color="light" className="text-dark">{index + 1}</Badge></td>
                                  <td className="px-3 py-3"><span className="fw-medium">{item.referenceNumber || '-'}</span></td>
                                  <td className="px-3 py-3"><Badge color="info" className="px-2 py-1">{item.quantity || 0}</Badge></td>
                                  <td className="px-3 py-3">
                                    <Badge color="success" className="px-2 py-1">{t('shared.installed', 'Installed')}</Badge>
                                  </td>
                                  <td className="px-3 py-3 text-muted">{item.createdAt ? formatCreatedDate(item.createdAt) : '-'}</td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted small mb-0">{t('business.spare_parts.no_consumption_records', 'No consumption records found')}</p>
                      )}
                    </CardBody>
                  </Card>
                </>
              ) : (
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="bx bx-list-ul text-muted" style={{ fontSize: '64px', opacity: 0.3 }}></i>
                      </div>
                      <h6 className="text-muted">{t('shared.no_inventory_records', 'No Inventory Records')}</h6>
                      <p className="text-muted small mb-0">{t('business.components.spare_parts_modal.no_inventory_records_desc', 'No inventory log entries found for this spare part')}</p>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          </TabPane>
        </TabContent>
      </div>

      {/* Modal Footer */}
      <div className="modal-footer border-top bg-light">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={toggle}
        >
          <i className="bx bx-x me-1"></i>
          {t('shared.close', 'Close')}
        </button>
      </div>

      {/* Asset Detail Modal */}
      {assetModal.isOpen && (
        <AssetModal
          isOpen={assetModal.isOpen}
          toggle={() => setAssetModal({ isOpen: false, userData: null })}
          userData={assetModal.userData}
          loading={false}
          formatCreatedDate={formatCreatedDate}
        />
      )}
    </Modal>
  );
};

export default SparePartsModal;
